import React, { useState } from 'react';
import { 
  MapPin, 
  FileText, 
  Search,
  Filter,
  Plus,
  Edit2,
  Trash2,
  Eye,
  Download,
  Upload,
  Map,
  CheckCircle,
  Clock,
  XCircle,
  TrendingUp,
  BarChart3
} from 'lucide-react';

const Land = () => {
  const [activeTab, setActiveTab] = useState('records');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [records, setRecords] = useState([
    { id: 1, surveyNo: 'SV-2024-001', location: 'Chennai District', area: '2.5', unit: 'acres', owner: 'Rajesh Kumar', status: 'verified', lastUpdated: '2024-11-10' },
    { id: 2, surveyNo: 'SV-2024-002', location: 'Coimbatore', area: '5.0', unit: 'acres', owner: 'Priya Singh', status: 'pending', lastUpdated: '2024-11-12' },
    { id: 3, surveyNo: 'SV-2024-003', location: 'Madurai', area: '3.2', unit: 'acres', owner: 'Amit Patel', status: 'verified', lastUpdated: '2024-11-08' },
    { id: 4, surveyNo: 'SV-2024-004', location: 'Salem', area: '1.8', unit: 'acres', owner: 'Lakshmi Iyer', status: 'disputed', lastUpdated: '2024-11-15' },
  ]);

  const [applications, setApplications] = useState([
    { id: 1, appNo: 'APP-001', type: 'Subdivision', applicant: 'Kumar Industries', surveyNo: 'SV-2024-001', date: '2024-11-10', status: 'pending' },
    { id: 2, appNo: 'APP-002', type: 'Transfer', applicant: 'Singh Enterprises', surveyNo: 'SV-2024-002', date: '2024-11-12', status: 'approved' },
    { id: 3, appNo: 'APP-003', type: 'Mutation', applicant: 'Patel Holdings', surveyNo: 'SV-2024-003', date: '2024-11-15', status: 'rejected' },
    { id: 4, appNo: 'APP-004', type: 'Conversion', applicant: 'Reddy Group', surveyNo: 'SV-2024-004', date: '2024-11-16', status: 'pending' },
  ]);

  const stats = {
    totalRecords: records.length,
    verified: records.filter(r => r.status === 'verified').length,
    pending: records.filter(r => r.status === 'pending').length,
    totalArea: records.reduce((sum, r) => sum + parseFloat(r.area), 0).toFixed(1),
    pendingApps: applications.filter(a => a.status === 'pending').length,
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'verified': 
      case 'approved': 
        return <CheckCircle className="w-4 h-4" />;
      case 'pending': 
        return <Clock className="w-4 h-4" />;
      case 'disputed': 
      case 'rejected': 
        return <XCircle className="w-4 h-4" />;
      default: 
        return <Clock className="w-4 h-4" />;
    }
  };

  const filteredRecords = records.filter(record => {
    const matchesSearch = record.surveyNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          record.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          record.owner.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || record.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const filteredApplications = applications.filter(app => {
    const matchesSearch = app.appNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          app.applicant.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || app.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Land Management System</h1>
          <p className="text-gray-600 mt-1">Manage land records, surveys, and applications</p>
        </div>
        <button className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#3d82c4] hover:bg-[#2d6fad] text-white rounded-lg font-medium transition-colors shadow-sm">
          <Plus className="w-5 h-5" />
          {activeTab === 'records' ? 'Add Record' : activeTab === 'applications' ? 'New Application' : 'Add Data'}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Total Records</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">{stats.totalRecords}</p>
            </div>
            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-[#3d82c4]" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Verified</p>
              <p className="text-3xl font-bold text-green-600 mt-2">{stats.verified}</p>
            </div>
            <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Pending</p>
              <p className="text-3xl font-bold text-yellow-600 mt-2">{stats.pending}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-50 rounded-xl flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Total Area</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">{stats.totalArea}</p>
              <p className="text-xs text-gray-500 mt-1">acres</p>
            </div>
            <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
              <MapPin className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Pending Apps</p>
              <p className="text-3xl font-bold text-orange-600 mt-2">{stats.pendingApps}</p>
            </div>
            <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-lg border border-gray-200">
        {/* Tabs */}
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex gap-6">
            {['records', 'applications', 'survey'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`
                  pb-3 px-1 font-medium text-sm border-b-2 transition-colors capitalize
                  ${activeTab === tab
                    ? 'border-[#3d82c4] text-[#3d82c4]'
                    : 'border-transparent text-gray-600 hover:text-gray-800'
                  }
                `}
              >
                {tab === 'records' ? 'Land Records' : tab === 'applications' ? 'Applications' : 'Survey Data'}
              </button>
            ))}
          </div>
        </div>

        {/* Search & Filter Bar */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={`Search ${activeTab}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3d82c4] focus:border-transparent"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3d82c4] focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="verified">Verified</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="disputed">Disputed</option>
              </select>
              <button className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
                <Download className="w-5 h-5" />
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'records' && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Survey No.</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Location</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Area</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Owner</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Last Updated</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map(record => (
                    <tr key={record.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-4">
                        <span className="font-semibold text-[#3d82c4]">{record.surveyNo}</span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-700">{record.location}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm font-medium text-gray-800">{record.area} {record.unit}</span>
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-700">{record.owner}</td>
                      <td className="py-4 px-4">
                        <span className={`
                          inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium capitalize
                          ${record.status === 'verified' ? 'bg-green-100 text-green-700' :
                            record.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }
                        `}>
                          {getStatusIcon(record.status)}
                          {record.status}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-600">{record.lastUpdated}</td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-end gap-2">
                          <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'applications' && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">App. No.</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Type</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Applicant</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Survey No.</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApplications.map(app => (
                    <tr key={app.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-4">
                        <span className="font-semibold text-[#3d82c4]">{app.appNo}</span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="inline-flex px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                          {app.type}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-700">{app.applicant}</td>
                      <td className="py-4 px-4 text-sm text-gray-600">{app.surveyNo}</td>
                      <td className="py-4 px-4 text-sm text-gray-600">{app.date}</td>
                      <td className="py-4 px-4">
                        <span className={`
                          inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium capitalize
                          ${app.status === 'approved' ? 'bg-green-100 text-green-700' :
                            app.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }
                        `}>
                          {getStatusIcon(app.status)}
                          {app.status}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-end gap-2">
                          <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors">
                            <CheckCircle className="w-4 h-4" />
                          </button>
                          <button className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                            <XCircle className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'survey' && (
            <div className="space-y-6">
              {/* Survey Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-800">Total Surveys</h3>
                    <BarChart3 className="w-6 h-6 text-[#3d82c4]" />
                  </div>
                  <p className="text-3xl font-bold text-gray-800">{records.length}</p>
                  <p className="text-sm text-gray-600 mt-2">Completed surveys</p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-800">Verified Records</h3>
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <p className="text-3xl font-bold text-gray-800">{stats.verified}</p>
                  <p className="text-sm text-gray-600 mt-2">Quality assured</p>
                </div>

                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-800">Pending Verification</h3>
                    <Clock className="w-6 h-6 text-yellow-600" />
                  </div>
                  <p className="text-3xl font-bold text-gray-800">{stats.pending}</p>
                  <p className="text-sm text-gray-600 mt-2">In review queue</p>
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="bg-gray-100 rounded-lg p-12 text-center border-2 border-dashed border-gray-300">
                <Map className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Survey Map View</h3>
                <p className="text-gray-600 mb-4">GIS integration and interactive maps would be displayed here</p>
                <button className="inline-flex items-center gap-2 px-4 py-2 bg-[#3d82c4] hover:bg-[#2d6fad] text-white rounded-lg font-medium transition-colors">
                  <Upload className="w-5 h-5" />
                  Upload Survey Data
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Land;